/**
 * Galaxy event section (sections/galaxy-event.liquid).
 *
 * <galaxy-event-countdown>: counts down to the event opening, then to its close. After the close it
 * hides itself, or the whole section when data-hide-section is set (never in the theme editor).
 * <galaxy-event-stars>: a faint star field drawn on a canvas over the video. Stars twinkle only while
 * on screen, and stay still for visitors who prefer reduced motion.
 */

const UNITS = /** @type {const} */ (['days', 'hours', 'minutes', 'seconds']);

/**
 * Epoch ms of a wall-clock date + time ("2026-10-29T00:00") in an IANA time zone, daylight saving
 * included. Same logic as assets/galaxy-countdown.js.
 * @param {string} local
 * @param {string} timeZone
 */
function zonedTime(local, timeZone) {
  const [date = '', time = '00:00'] = local.split('T');
  const [y, m, d] = date.split('-').map(Number);
  const [hh = 0, mm = 0] = time.split(':').map(Number);
  if (!y || !m || !d) return NaN;
  const wall = Date.UTC(y, m - 1, d, hh, mm);

  let format;
  try {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
  } catch {
    return wall; // unknown time zone: fall back to UTC
  }

  /** @param {number} t */
  const offset = (t) => {
    /** @type {Record<string, number>} */
    const p = {};
    for (const { type, value } of format.formatToParts(t)) p[type] = Number(value);
    return Date.UTC(p.year ?? 0, (p.month ?? 1) - 1, p.day, p.hour, p.minute, p.second) - t;
  };

  let t = wall - offset(wall);
  t = wall - offset(t);
  return t;
}

/** @param {number} n */
const pad = (n) => String(n).padStart(2, '0');

class GalaxyEventCountdown extends HTMLElement {
  #timer = 0;
  #start = NaN;
  #end = NaN;
  #summary = '';

  connectedCallback() {
    const zone = this.dataset.timeZone || 'Africa/Cairo';
    this.#start = zonedTime(this.dataset.start ?? '', zone);
    this.#end = zonedTime(this.dataset.end ?? '', zone);
    this.#tick();
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = () => {
    clearTimeout(this.#timer);
    if (!document.hidden) this.#tick();
  };

  #tick = () => {
    const now = Date.now();
    const designMode = this.hasAttribute('data-design-mode');
    let target = this.#start;
    let label = this.dataset.labelBefore;

    if (!(now < this.#start)) {
      target = this.#end;
      label = this.dataset.labelDuring;
    }

    if (!(now < this.#end) || Number.isNaN(target)) {
      if (!designMode) {
        const section = this.hasAttribute('data-hide-section') ? this.closest('.shopify-section') : null;
        (section instanceof HTMLElement ? section : this).hidden = true;
        return;
      }
      target = now;
      label = this.dataset.labelAfter;
    }

    const status = this.querySelector('[data-status]');
    if (status && status.textContent !== label) status.textContent = label ?? '';

    const left = Math.max(0, Math.floor((target - now) / 1000));
    const values = {
      days: Math.floor(left / 86400),
      hours: Math.floor((left % 86400) / 3600),
      minutes: Math.floor((left % 3600) / 60),
      seconds: left % 60,
    };
    for (const unit of UNITS) {
      const el = this.querySelector(`[data-unit="${unit}"]`);
      const text = pad(values[unit]);
      if (el && el.textContent !== text) el.textContent = text;
    }

    // Screen readers get one sentence that changes once a minute, not every second.
    const summary = this.querySelector('[data-summary]');
    if (summary instanceof HTMLElement) {
      const [d = 'Days', h = 'Hours', m = 'Minutes'] = (summary.dataset.units ?? '').split('|');
      const text = `${label ?? ''}: ${values.days} ${d}, ${values.hours} ${h}, ${values.minutes} ${m}`;
      if (text !== this.#summary) summary.textContent = this.#summary = text;
    }

    this.#timer = window.setTimeout(this.#tick, 1000 - (now % 1000) + 20);
  };
}

class GalaxyEventStars extends HTMLElement {
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {CanvasRenderingContext2D | null} */
  #ctx = null;
  /** @type {{x: number, y: number, r: number, a: number, phase: number, speed: number, warm: boolean}[]} */
  #stars = [];
  #frame = 0;
  #last = 0;
  #visible = false;
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /** @type {ResizeObserver | null} */
  #resize = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#canvas = document.createElement('canvas');
    this.#canvas.setAttribute('aria-hidden', 'true');
    this.append(this.#canvas);
    this.#ctx = this.#canvas.getContext('2d');

    this.#resize = new ResizeObserver(() => this.#build());
    this.#resize.observe(this);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      this.#loop();
    });
    this.#observer.observe(this);
    document.addEventListener('visibilitychange', this.#loop);
  }

  disconnectedCallback() {
    this.#resize?.disconnect();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    document.removeEventListener('visibilitychange', this.#loop);
    this.#canvas?.remove();
  }

  get #twinkle() {
    return this.hasAttribute('data-twinkle') && !this.#reduced;
  }

  /* One star per ~N px² of the section, where N falls as the density setting rises. */
  #build() {
    const canvas = this.#canvas;
    if (!canvas) return;
    const { width, height } = this.getBoundingClientRect();
    if (!width || !height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    this.#ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);

    const density = Number(this.dataset.density) || 50;
    const count = Math.min(900, Math.round(((width * height) / 100000) * density * 0.6));
    this.#stars = Array.from({ length: count }, () => {
      const bright = Math.random() < 0.06;
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        r: bright ? 0.9 + Math.random() * 0.7 : 0.25 + Math.random() * 0.6,
        a: bright ? 0.7 + Math.random() * 0.3 : 0.15 + Math.random() * 0.55,
        phase: Math.random() * Math.PI * 2,
        speed: 0.4 + Math.random() * 1.2,
        warm: Math.random() < 0.18,
      };
    });
    this.#draw(performance.now());
  }

  /** @param {number} time */
  #draw(time) {
    const ctx = this.#ctx;
    const canvas = this.#canvas;
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const warm = this.dataset.warm || '#ece1cd';
    const t = time / 1000;
    for (const s of this.#stars) {
      const flicker = this.#twinkle ? 0.55 + 0.45 * Math.sin(t * s.speed + s.phase) : 1;
      ctx.globalAlpha = s.a * flicker;
      ctx.fillStyle = s.warm ? warm : '#ffffff';
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
      // A soft halo around the few bright stars.
      if (s.r > 0.9) {
        ctx.globalAlpha = s.a * flicker * 0.15;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  /* Redraw at ~24 fps, only while on screen, the tab is visible and twinkling is on. */
  #loop = () => {
    cancelAnimationFrame(this.#frame);
    if (!this.#twinkle || !this.#visible || document.hidden) return;
    /** @param {number} time */
    const step = (time) => {
      if (time - this.#last > 42) {
        this.#last = time;
        this.#draw(time);
      }
      this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  };
}

if (!customElements.get('galaxy-event-countdown')) {
  customElements.define('galaxy-event-countdown', GalaxyEventCountdown);
}

if (!customElements.get('galaxy-event-stars')) {
  customElements.define('galaxy-event-stars', GalaxyEventStars);
}
